// LooksPostPage.js

import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  Fade,
  Paper,
  List,
  ListItem,
  Typography,
  useMediaQuery
} from '@mui/material';

import { BsMegaphone } from 'react-icons/bs';
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  FaRegPaperPlane,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaFacebook,
  FaSoundcloud
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';          // ← NEW
import { AiOutlineZoomIn } from 'react-icons/ai';
import { GoAlert } from 'react-icons/go';
import { CiViewList } from 'react-icons/ci';
import { IoMailOutline } from 'react-icons/io5';
import LooksReportForm from './LooksReportForm';
import { LooksReportsContext } from '../contexts/LooksReportsContext';

import { useAuth } from '../contexts/AuthContext';
import { LooksPostsContext } from '../contexts/LooksPostsContext';
import { timeAgo } from '../utils/timeFormatter';
import { db } from '../utils/firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import {
  incrementPageViewCount,
  incrementLinkClickCount,
  incrementInstagramClickCount,
  incrementTwitterClickCount,
  incrementTiktokClickCount,
  incrementYoutubeClickCount,
  incrementFacebookClickCount,
  incrementSoundcloudClickCount,
  getPostById
} from '../utils/dbUtilities';

import VisibilityTracker from './VisibilityTracker';
import SharePost from './SharePost';

import { styled } from '@mui/material/styles';
import { Card, CardContent } from '@mui/material';

import './Post.css';

// For 2-minute polling of Firestore analytics
const TWO_MINUTES = 2 * 60 * 1000;

// helper for teaser
const MAX_RAW = 120;

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  border: 'none',
  overflow: 'hidden',
  margin: '24px auto',
  width: '100%',
  maxWidth: '550px',
  minWidth: window.innerWidth <= 1180 ? '300px' : '550px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  height: 'auto',
  minHeight: '300px',
  transition: 'max-width 0.3s ease-in-out, padding 0.3s ease-in-out',
  '& > *': {
    width: '100%',
    boxSizing: 'border-box'
  },
  '& .MuiCardMedia-root': {
    border: 'none',
    outline: 'none',
    boxShadow: 'none'
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '300px',
    maxWidth: '100%'
  }
}));

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  width: '100%'
});

const StyledTitle = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '8px',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer'
});

// Helper to split text into bullet points
const splitText = (text) => {
  if (!text) return [];
  return text.split(/(?<=[.?!])\s+(?=[A-Z])/);
};

// Helper to shorten URLs
const shortenUrl = (url) => {
  const urlWithoutPrefix = url.replace(/^https?:\/\/(www\.)?/i, '');
  const maxLength = 25;
  return urlWithoutPrefix.length > maxLength
    ? `${urlWithoutPrefix.slice(0, maxLength)}...`
    : urlWithoutPrefix;
};

/* Ice-breaker label map (matches LooksPostForm.js) */
const ICE_BREAKER_LABELS = {
  origin:            'Where are you from?',
  favoriteFood:      "What's your favorite food?",
  favCountryVisit:   'Favorite country you’ve visited?',
  musicGenre:        'Which song or artist have you been playing on repeat lately?',
  dreamMeet:         'If you could meet anyone (dead or alive), who would it be?',
  favDessert:        "What's your favorite dessert?",
  millionOrBillion:  'Would you take $1 million guaranteed or flip a coin for $1 billion?'
};

/* Keep the display order identical to LooksPostForm.js */
const ICE_BREAKER_ORDER = [
  'origin',
  'favoriteFood',
  'favCountryVisit',
  'musicGenre',
  'dreamMeet',
  'favDessert',
  'millionOrBillion'
];

/* ---------- tiny zoom-modal (copied from LooksPost.js) ---------- */
const SAFE_MARGIN = 96;

const ZoomModal = ({ src, onClose }) => (
  <div
    role="dialog"
    aria-modal="true"
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'relative',
        maxWidth: `calc(100vmin - ${SAFE_MARGIN}px)`,
        maxHeight: `calc(100vmin - ${SAFE_MARGIN}px)`
      }}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'rgba(0,0,0,.6)',
          color: '#fff',
          border: 'none',
          borderRadius: '50%',
          width: 32,
          height: 32,
          fontSize: 22,
          cursor: 'pointer',
          lineHeight: 0
        }}
      >
        ×
      </button>

      <img
        src={src}
        alt="Profile full"
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block'
        }}
      />
    </div>
  </div>
);

const LooksPostPage = () => {
  const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const isMediumScreen = useMediaQuery('(min-width: 601px) and (max-width: 1024px)');
  const isSurfacePro7 = useMediaQuery('(min-width: 1368px) and (max-width: 1368px)');

  const { id } = useParams();
  const navigate = useNavigate();
  const { posts, deletePostById } = useContext(LooksPostsContext);
  const { reportPost } = useContext(LooksReportsContext);
  const { currentUser, isLoggedIn, isAdmin } = useAuth();

  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleted, setIsDeleted] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isAuthor, setIsAuthor] = useState(false);

  const [showReportForm, setShowReportForm] = useState(false);
  const [showContactList, setShowContactList] = useState(false);
  const [showSharePost, setShowSharePost] = useState(false);

  /* 🔍 zoom state */
  const [zoomUrl, setZoomUrl] = useState(null);

  /* full-size image picker (same logic used in LooksPost.js) */
  const getFullUrl = useCallback(() => {
    if (post?.profileImageFullUrl) return post.profileImageFullUrl;           // preferred
    if (post?.profileImageUrl?.includes('looksProfileImagesAvatar/')) {
      return post.profileImageUrl.replace(
        'looksProfileImagesAvatar/',
        'looksProfileImagesFull/'
      );
    }
    return post?.profileImageUrl;                                             // fallback
  }, [post]);

  // For visited-link styling, not local impressions
  const [clickedPosts, setClickedPosts] = useState(() => {
    const saved = localStorage.getItem('clickedPosts');
    return saved ? JSON.parse(saved) : {};
  });

  // For "Discover Posts"
  const [randomPostsKey, setRandomPostsKey] = useState(0);

  // On mount or when ID changes, fetch or find post
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!id) {
      setIsLoading(false);
      return;
    }

    // Check if this post is in context already
    const foundInContext = posts.find((p) => p.id?.toString() === id);
    if (foundInContext) {
      setPost(foundInContext);
      setIsLoading(false);
    } else {
      // else fetch from Firestore
      (async () => {
        try {
          const docFromDb = await getPostById(id, 'looksPosts');
          setPost(docFromDb || null);
        } catch (err) {
          console.error('Error fetching older post by ID:', err);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [posts, id]);

  // Once we know which post, check if it's deleted or if currentUser is author
  useEffect(() => {
    if (!isLoading && post) {
      if (post.deleted) {
        setIsDeleted(true);
      } else if (currentUser && currentUser.displayName === post.author) {
        setIsAuthor(true);
      }
    } else if (!isLoading && !post) {
      setIsDeleted(true);
    }
  }, [isLoading, post, currentUser]);

  // Increment page view on mount
  useEffect(() => {
    if (post?.id) {
      incrementPageViewCount(post.id, 'looksPosts');
    }
  }, [post?.id]);

  // Real-time listen for likes
  useEffect(() => {
    if (!post?.id) return;
    const likesQueryRef = query(collection(db, 'looksLikes'), where('postId', '==', post.id));
    const unsubscribe = onSnapshot(likesQueryRef, (snapshot) => {
      const likesData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setLikeCount(likesData.length);

      if (currentUser) {
        const userLike = likesData.some((like) => like.userId === currentUser.uid);
        setPost((prev) => (prev ? { ...prev, liked: userLike } : null));
      }
    });
    return () => unsubscribe();
  }, [post, currentUser]);

  // Poll Firestore every 2 minutes to keep analytics up-to-date
  const fetchPostData = useCallback(async () => {
    if (!post?.id) return;
    try {
      const docRef = doc(db, 'looksPosts', post.id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPost((prev) =>
          prev
            ? {
                ...prev,
                viewCount: data.viewCount || 0,
                linkClickCount: data.linkClickCount || 0,
                impressionCount: data.impressionCount || 0,
                instagramClickCount: data.instagramClickCount || 0,
                twitterClickCount: data.twitterClickCount || 0,
                tiktokClickCount: data.tiktokClickCount || 0,
                youtubeClickCount: data.youtubeClickCount || 0,
                facebookClickCount: data.facebookClickCount || 0,
                soundcloudClickCount: data.soundcloudClickCount || 0,
                isReported: data.isReported || false,
                feedImpressionCount: data.feedImpressionCount || 0,
                discoveryBoxImpressionCount: data.discoveryBoxImpressionCount || 0,
                pageViewCount: data.pageViewCount || 0
              }
            : prev
        );
      }
    } catch (err) {
      console.error('Error polling post data:', err);
    }
  }, [post?.id]);

  useEffect(() => {
    if (!post?.id) return;
    fetchPostData();
    const intervalId = setInterval(() => {
      fetchPostData();
    }, TWO_MINUTES);
    return () => clearInterval(intervalId);
  }, [post?.id, fetchPostData]);

  // Keep localStorage "clickedPosts" in sync
  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem('clickedPosts');
      if (saved) {
        setClickedPosts(JSON.parse(saved));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Generate random post suggestions (excluding the current one)
  const randomPosts = useMemo(() => {
    const validPosts = posts.filter((p) => p.id !== id && !p.deleted && !p.isReported);
    const shuffled = [...validPosts].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  }, [posts, id, randomPostsKey]);

  const handleRefresh = () => {
    setRandomPostsKey((prev) => prev + 1);
  };

  // Only author or admin sees likeCount
  const canSeeLikesCount = useCallback(() => {
    return isAuthor || isAdmin;
  }, [isAuthor, isAdmin]);

  if (isLoading) {
    return null; // or a spinner, etc.
  }

  if (isDeleted || !post) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 2,
          pt: { xs: 10, sm: 12, md: 15, lg: 15, xl: 15 },
          ml: isSurfacePro7 ? '80px' : isMediumScreen ? '60px' : { sm: '140px' },
          '@media (min-width: 1030px) and (max-width: 1300px)': { mt: -5 }
        }}
        className="post-page-container"
      >
        <Typography variant="h6">This Post No Longer Exists</Typography>
      </Box>
    );
  }

  // If the post is reported & user not admin => disclaim
  if (post.isReported && !isAdmin) {
    return (
      <VisibilityTracker postId={id} isDiscoveryBox={false} isPostAuthor={false}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 2,
            pt: { xs: 10, sm: 12, md: 15, lg: 8, xl: 8 },
            ml: isSurfacePro7 ? '80px' : isMediumScreen ? '60px' : { sm: '140px' }
          }}
          className="post-page-container"
        >
          <StyledCard
            sx={{
              margin: '24px auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px'
            }}
          >
            <CardContent
              sx={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                Reported Post Under Review
              </Typography>
              <Typography variant="body1">
                This post has been flagged and is under Admin review.
              </Typography>
            </CardContent>
          </StyledCard>
        </Box>
      </VisibilityTracker>
    );
  }

  const handlePostClick = () => {
    const updated = { ...clickedPosts, [post.id]: true };
    setClickedPosts(updated);
    localStorage.setItem('clickedPosts', JSON.stringify(updated));
  };

  // Like / Unlike logic
  const handleLike = async () => {
    if (!isLoggedIn || !currentUser) {
      navigate('/login');
      return;
    }

        /* ---------- optimistic UI ---------- */
        setPost((p) => (p ? { ...p, liked: !p.liked } : p));      // flip the liked flag locally
        setLikeCount((prev) => (post?.liked ? prev - 1 : prev + 1)); // bump the number right away
    
        if (post.liked) {
          // ----- UNLIKE -----
          const likeQueryRef = query(
            collection(db, 'looksLikes'),
            where('postId', '==', post.id),
            where('userId', '==', currentUser.uid)
          );
          const snapshot = await getDocs(likeQueryRef);
          const likeDoc = snapshot.docs[0];
          if (likeDoc) {
            await deleteDoc(likeDoc.ref);
          }
        } else {
          // ----- LIKE -----
          await addDoc(collection(db, 'looksLikes'), {
            postId: post.id,
            userId: currentUser.uid,
            timestamp: new Date()
          });
        }
      };
    
      // Delete post
      const handleRemove = async () => {
        if (window.confirm('Are you sure you want to delete this post?')) {
          try {
            await deletePostById(id);
            setIsDeleted(true);
          } catch (error) {
            console.error('Error deleting post:', error);
          }
        }
      };
    
      // Toggle forms
      const toggleReportForm = (e) => {
        e.stopPropagation();
        if (!isLoggedIn) {
          navigate('/login');
          return;
        }
        setShowReportForm((prev) => !prev);
      };
    
      const toggleContactList = (e) => {
        e.stopPropagation();
        if (!isLoggedIn) {
          navigate('/login');
          return;
        }
        setShowContactList((prev) => !prev);
      };
      const closeContactList = () => setShowContactList(false);
    
      // Category, userName, location clicks
      const handleUsernameClick = (e) => {
        e.stopPropagation();
        navigate(`/looksusersposts/${post.author}`);
      };
      const navigateToCityPosts = (city) => navigate(`/cityposts/${city}`);
      const navigateToStatePosts = (state) => navigate(`/stateposts/${state}`);
      const navigateToCountryPosts = (country) => navigate(`/countryposts/${country}`);
      const navigateToLocationPosts = (loc) => navigate(`/locationposts/${loc}`);
    
      // Link & social clicks
      const handleLinkClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (post?.id) await incrementLinkClickCount(post.id, 'looksPosts');
        const href = post.link.includes('http') ? post.link : `https://${post.link}`;
        window.open(href, '_blank', 'noopener,noreferrer');
      };
    
      const handleInstagramClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (post?.id) await incrementInstagramClickCount(post.id, 'looksPosts');
        window.open(`https://instagram.com/${post.instagram.replace('@', '')}`, '_blank');
      };
    
      const handleTwitterClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (post?.id) await incrementTwitterClickCount(post.id, 'looksPosts');
        window.open(`https://twitter.com/${post.twitter.replace('@', '')}`, '_blank');
      };
    
      const handleTiktokClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (post?.id) await incrementTiktokClickCount(post.id, 'looksPosts');
        window.open(`https://tiktok.com/@${post.tiktok.replace('@', '')}`, '_blank');
      };
    
      const handleYoutubeClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (post?.id) await incrementYoutubeClickCount(post.id, 'looksPosts');
        window.open(`https://youtube.com/@${post.youtube.replace('@', '')}`, '_blank');
      };
    
      const handleFacebookClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (post?.id) await incrementFacebookClickCount(post.id, 'looksPosts');
        window.open(`https://www.facebook.com/${post.facebook}`, '_blank');
      };

      const handleSoundcloudClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (post?.id) await incrementSoundcloudClickCount(post.id, 'looksPosts');  // ★ add
        window.open(
          `https://soundcloud.com/${post.soundcloud.replace('@', '')}`,
          '_blank',
          'noopener,noreferrer'
        );
      };      
        
      // Timestamps
      const postDate = post.timestamp?.toDate ? post.timestamp.toDate() : post.timestamp;
      const lastEditedDate = post.lastEdited?.toDate ? post.lastEdited.toDate() : post.lastEdited;
    
      return (
        <VisibilityTracker
          postId={id}
          isDiscoveryBox={false}
          isPostAuthor={currentUser?.displayName === post.author}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 2,
              pt: { xs: 10, sm: 12, md: 15, lg: 15, xl: 15 },
              ml: isSurfacePro7 ? '80px' : isMediumScreen ? '60px' : { sm: '140px' },
              '@media (min-width: 1030px) and (max-width: 1300px)': {
                mt: -5
              }
            }}
            className="post-page-container"
          >
            <StyledCard
              onClick={handlePostClick}
              sx={{
                width: '100%',
                maxWidth: '800px',
                mb: 2
              }}
            >
              <StyledCardContent>
                {/* ---------- Headline (userName) & like ---------- */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <StyledTitle
                    onClick={(e) => {
                      e.stopPropagation();
                      const updated = { ...clickedPosts, [post.id]: true };
                      setClickedPosts(updated);
                      localStorage.setItem('clickedPosts', JSON.stringify(updated));
                    }}
                    sx={{
                      color: clickedPosts[post.id] ? '#551A8B' : '#1a0dab',
                      marginBottom: 0
                    }}
                  >
                    {post.userName || 'Anonymous'}
                  </StyledTitle>
    
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={handleLike}>
                      {post.liked ? (
                        <Favorite sx={{ color: '#ed4956' }} />
                      ) : (
                        <FavoriteBorder sx={{ color: '#262626' }} />
                      )}
                    </IconButton>
                    {canSeeLikesCount() && (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, textAlign: 'center', minWidth: '15px' }}
                      >
                        {likeCount}
                      </Typography>
                    )}
                  </Box>
                </Box>
    
                <Box sx={{ mb: 1 }} />
    
                {/* ---------- Profile image (click to zoom) ---------- */}
                {post.profileImageUrl && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2, position: 'relative' }}>
                    {/* avatar */}
                    <Box
                      sx={{
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomUrl(getFullUrl());
                      }}
                    >
                      <img
                        src={post.profileImageUrl}
                        alt="Profile avatar"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>

                    {/* zoom-in overlay icon (hidden on XS) */}
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomUrl(getFullUrl());
                      }}
                      sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        display: { xs: 'none', sm: 'flex' },
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'rgba(0,0,0,.6)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <AiOutlineZoomIn style={{ color: '#fff', fontSize: 20 }} />
                    </Box>
                  </Box>
                )}
    
                {/* ---------- "Who Are You?" heading ---------- */}
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'left',
                    fontWeight: 'bold',
                    mt: 2,
                    mb: 1,
                    color: '#000'
                  }}
                >
                  Who Are You?
                </Typography>
    
                {/* ---------- Description ---------- */}
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1.125rem', md: '1.1875rem' }, // ⬆︎ 18‑19 px
                    lineHeight: 1.55,
                    whiteSpace: 'pre-line',
                    mt: 1,
                    textAlign: 'left'
                  }}
                >
                  {splitText(post.description).length > 1 ? (
                    <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                      {splitText(post.description).map((sentence, idx) => (
                        <li key={idx}>{sentence.trim()}</li>
                      ))}
                    </ul>
                  ) : (
                    post.description
                  )}
                </Typography>
    
                {/* ---------- Ice-breakers ---------- */}
                {post.iceBreakers && Object.keys(post.iceBreakers).length > 0 && (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      Fun Facts
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      {ICE_BREAKER_ORDER.map((id) => {
                        const answer = post.iceBreakers[id];
                        if (!answer) return null;
                        return (
                          <Typography
                            key={id}
                            variant="body2"
                            sx={{
                              fontSize: { xs: '.95rem', md: '1rem' }, // ⬆︎ 15‑16 px
                              lineHeight: 1.5,
                              mb: 0.5,
                              textAlign: 'left'
                            }}
                          >
                            <strong>{ICE_BREAKER_LABELS[id]}</strong> {answer}
                          </Typography>
                        );
                      })}
                    </Box>
                  </>
                )}
    
                {/* Where Are You? */}
                {(post.city || post.state || post.country || post.location) && (
                  <Box sx={{ mt: 3, mb: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        textAlign: 'left',
                        color: '#000',
                        mb: 1
                      }}
                    >
                      Where Are You?
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '1rem',
                        textAlign: 'left',
                        display: 'inline-block',
                        lineHeight: '1.4'
                      }}
                    >
                      {post.city && (
                        <>
                          <span
                            className="location-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToCityPosts(post.city);
                            }}
                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                          >
                            {post.city}
                          </span>
                        </>
                      )}
                      {post.city && post.state ? ', ' : ''}
                      {post.state && (
                        <>
                          <span
                            className="location-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToStatePosts(post.state);
                            }}
                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                          >
                            {post.state}
                          </span>
                        </>
                      )}
                      {(post.city || post.state) && post.country ? ', ' : ''}
                      {post.country && (
                        <>
                          <span
                            className="location-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToCountryPosts(post.country);
                            }}
                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                          >
                            {post.country}
                          </span>
                        </>
                      )}
                      {post.location && (
                        <>
                          {' '}
                          (
                          <span
                            className="location-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToLocationPosts(post.location);
                            }}
                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                          >
                            {post.location}
                          </span>
                          )
                        </>
                      )}
                    </Typography>
                  </Box>
                )}
    
                {/* Link */}
                {post.link && (
                  <Box
                    sx={{
                      mt: 0,
                      mb: 1,
                      width: '100%',
                      textAlign: 'left'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        color: '#1a0dab',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        fontSize: '1rem'
                      }}
                      onClick={handleLinkClick}
                    >
                      {shortenUrl(post.link)}
                    </Typography>
                  </Box>
                )}
    
                {/* Posted by + Last Edited */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  onClick={handleUsernameClick}
                  sx={{
                    cursor: 'pointer',
                    mb: lastEditedDate ? 0.5 : 2,
                    textAlign: 'left',
                    mt: post.link ? 0 : 1
                  }}
                >
                  Posted {timeAgo(postDate)} by{' '}
                  <span
                    style={{
                      color: '#1a0dab',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      textDecoration: 'none'
                    }}
                  >
                    {post.author}
                  </span>
                </Typography>
                {lastEditedDate && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, textAlign: 'left' }}
                  >
                    Last Edited {timeAgo(lastEditedDate)}
                  </Typography>
                )}
    
                            {/* Total Views: sum of old + new fields */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                mt: 2,
                mb: 2
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '15px',
                  lineHeight: 1.4,
                  fontWeight: 700,
                  textAlign: 'center'
                }}
              >
                <span
                  style={{
                    backgroundColor: '#fff',
                    color: '#000',
                    fontSize: '15px',
                    padding: '3px 8px',
                    border: '2px solid #000',
                    borderRadius: '6px',
                    marginRight: '8px'
                  }}
                >
                  {(
                    (post.impressionCount || 0) +
                    (post.feedImpressionCount || 0) +
                    (post.discoveryBoxImpressionCount || 0) +
                    (post.pageViewCount || 0)
                  ).toLocaleString()}
                </span>
                 Views
              </Typography>
            </Box>

            {/* Goals */}
            {post.goals && Array.isArray(post.goals) && post.goals.length > 0 && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#000',
                    mt: 2,
                    mb: 1
                  }}
                >
                  What's The Goal?
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mt: 1 }}>
                  {post.goals.slice(0, 2).map((goal) => (
                    <Box
                      key={goal}
                      sx={{
                        cursor: 'default',
                        backgroundColor: '#e0f4ff',
                        p: '5px 12px',
                        borderRadius: '16px',
                        fontSize: '14px',
                        color: '#333',
                        border: '1px solid #99e0ff',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {goal}
                    </Box>
                  ))}
                </Box>

                {post.goals.length > 2 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '10px',
                      mt: 1,
                      mb: 2
                    }}
                  >
                    {post.goals.slice(2).map((goal) => (
                      <Box
                        key={goal}
                        sx={{
                          cursor: 'default',
                          backgroundColor: '#e0f4ff',
                          p: '5px 12px',
                          borderRadius: '16px',
                          fontSize: '14px',
                          color: '#333',
                          border: '1px solid #99e0ff',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {goal}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}

            {/* Follow the Growth */}
            {(post.instagram || post.twitter || post.tiktok || post.youtube || post.facebook || post.soundcloud) && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  mt: 3,
                  mb: 2,
                  width: '100%',
                  gap: 2
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: '#000',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Follow the Growth!
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {post.instagram && (
                    <FaInstagram
                      className="social-icon instagram"
                      onClick={handleInstagramClick}
                      style={{ fontSize: '28px', cursor: 'pointer' }}
                    />
                  )}
                  {post.twitter && (
                    <FaXTwitter
                      className="social-icon twitter"
                      onClick={handleTwitterClick}
                      style={{ fontSize: '28px', cursor: 'pointer' }}
                    />
                  )}
                  {post.tiktok && (
                    <FaTiktok
                      className="social-icon tiktok"
                      onClick={handleTiktokClick}
                      style={{ fontSize: '28px', cursor: 'pointer' }}
                    />
                  )}
                  {post.youtube && (
                    <FaYoutube
                      className="social-icon youtube"
                      onClick={handleYoutubeClick}
                      style={{ fontSize: '28px', cursor: 'pointer' }}
                    />
                  )}
                  {post.facebook && (
                    <FaFacebook
                      className="social-icon facebook"
                      onClick={handleFacebookClick}
                      style={{ fontSize: '28px', cursor: 'pointer' }}
                    />
                  )}
                  {post.soundcloud && (                             
                    <FaSoundcloud
                      className="social-icon soundcloud"
                      onClick={handleSoundcloudClick}
                      style={{ fontSize: '28px', cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Contact List, Share, Email, etc. */}
            <Box sx={{ mt: 4, mb: '10px', width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  mb: 2
                }}
              >
                <IconButton
                  onClick={toggleContactList}
                  sx={{ p: '4px', color: '#1a0dab', fontSize: '24px' }}
                >
                  <GoAlert style={{ fontSize: '24px' }} />
                </IconButton>
                <Typography
                  variant="body2"
                  onClick={toggleContactList}
                  sx={{
                    color: '#1a0dab',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  Email Updates Sign Up
                </Typography>
              </Box>

              {isAuthor && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    mb: 2
                  }}
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/view-contact-list');
                    }}
                    sx={{ p: '4px', color: '#1a0dab' }}
                  >
                    <CiViewList style={{ fontSize: '24px' }} />
                  </IconButton>
                  <Typography
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/view-contact-list');
                    }}
                    sx={{
                      color: '#1a0dab',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}
                  >
                    View Email Sign Ups
                  </Typography>
                </Box>
              )}

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSharePost(true);
                }}
              >
                <IconButton sx={{ p: '4px', color: '#1a0dab', fontSize: '24px' }}>
                  <BsMegaphone style={{ fontSize: '24px' }} />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ color: '#1a0dab', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer' }}
                >
                  Share this Post
                </Typography>
              </Box>
            </Box>

            {showSharePost && (
              <Box
                sx={{
                  position: 'relative',
                  p: '15px 20px',
                  pt: '50px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9',
                  mb: '20px'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSharePost(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    color: '#666',
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    fontWeight: 'bold'
                  }}
                  aria-label="Close Share Options"
                >
                  ×
                </button>
                <SharePost url={`${window.location.origin}/looksposts/${id}`} />
              </Box>
            )}

            {post.email && (
              <Box sx={{ mb: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`mailto:${post.email}`, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{ p: '4px', color: '#1a0dab', fontSize: '24px' }}
                >
                  <IoMailOutline style={{ fontSize: '24px' }} />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1a0dab',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`mailto:${post.email}`, '_blank', 'noopener,noreferrer');
                  }}
                >
                  Email
                </Typography>
              </Box>
            )}

            {/* Author's Post Analytics */}
            {isAuthor && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  p: '20px 16px',
                  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                  mt: '20px',
                  gap: '8px',
                  width: '100%',
                  backgroundColor: '#f8f9fa'
                }}
              >
                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                  Your Post Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  Post Page Views: {post.viewCount || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  URL Link Clicks: {post.linkClickCount || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  Post Impressions (Views in Feeds & Discovery Boxes): {post.impressionCount || 0}
                </Typography>
                {post.instagram && (
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    Instagram Clicks: {post.instagramClickCount || 0}
                  </Typography>
                )}
                {post.twitter && (
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    Twitter Clicks: {post.twitterClickCount || 0}
                  </Typography>
                )}
                {post.tiktok && (
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    TikTok Clicks: {post.tiktokClickCount || 0}
                  </Typography>
                )}
                {post.youtube && (
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    YouTube Clicks: {post.youtubeClickCount || 0}
                  </Typography>
                )}
                {post.facebook && (
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    Facebook Clicks: {post.facebookClickCount || 0}
                  </Typography>
                )}
                {post.soundcloud && (
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    SoundCloud Clicks: {post.soundcloudClickCount || 0}
                  </Typography>
                )}
              </Box>
            )}
          </StyledCardContent>

          {/* Bottom Bar: Edit / Delete (author or admin)  OR  Report */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            {isAuthor || isAdmin ? (
              <>
                {/* authors keep the Edit button */}
                {isAuthor && (
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/editlookspost/${id}`);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Delete">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Report">
                <IconButton onClick={toggleReportForm}>
                  <OutlinedFlagIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

                    {/* Report Form Modal */}
                    {showReportForm && (
            <Box onClick={(e) => e.stopPropagation()}>
              <LooksReportForm
                postId={id}
                submitReport={reportPost}
                closeModal={() => setShowReportForm(false)}
              />
            </Box>
          )}
        </StyledCard>

        {randomPosts.length > 0 && (
          <Fade in={true} timeout={800}>
            <Paper
              elevation={3}
              sx={{
                width: '100%',
                maxWidth: '800px',   // ⬅️ matches the post card
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 2,
                mt: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: '#fff'
              }}            
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1d1d1f', fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' } }}>
                  Discover Posts
                </Typography>
                <IconButton onClick={handleRefresh} sx={{ color: '#007aff' }}>
                  <RefreshIcon />
                </IconButton>
              </Box>

              <List sx={{ width: '100%', p: 0 }}>
                {randomPosts.map((randomPost) => (
                  <ListItem
                    key={randomPost.id}
                    sx={{
                      p: 0,
                      mb: 1,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box
                      onClick={() => navigate(`/looksposts/${randomPost.id}`)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        p: 1.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          color: clickedPosts[randomPost.id] ? '#551A8B' : '#1a0dab',
                          mb: 0.5
                        }}
                      >
                        {randomPost.userName || 'Anonymous'}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#444',
                          mb: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {randomPost.description?.length > MAX_RAW
                          ? `${randomPost.description.substring(0, MAX_RAW)}...`
                          : randomPost.description}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mt: 0.5
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontSize: '0.8rem' }}
                        >
                          {timeAgo(
                            randomPost.timestamp?.toDate
                              ? randomPost.timestamp.toDate()
                              : randomPost.timestamp
                          )}
                        </Typography>

                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Fade>
        )}

        {/* Contact List Form */}
        {showContactList && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={closeContactList}
          >
            <Box
              sx={{
                backgroundColor: '#fff',
                p: 3,
                borderRadius: 2,
                maxWidth: '90%',
                width: '400px',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                Sign Up for Updates
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
                Enter your email to receive updates from {post.author}
              </Typography>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target;
                  const email = form.email.value;
                  const name = form.name.value;

                  if (!email) {
                    alert('Please enter your email');
                    return;
                  }

                  try {
                    await addDoc(collection(db, 'contactList'), {
                      email,
                      name: name || '',
                      postId: post.id,
                      postAuthor: post.author,
                      timestamp: new Date()
                    });

                    alert('Thank you for signing up!');
                    closeContactList();
                  } catch (error) {
                    console.error('Error adding to contact list:', error);
                    alert('Error signing up. Please try again.');
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Email (required)
                  </Typography>
                  <input
                    type="email"
                    name="email"
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Name (optional)
                  </Typography>
                  <input
                    type="text"
                    name="name"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button
                    type="button"
                    onClick={closeContactList}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Sign Up
                  </button>
                </Box>
              </form>
            </Box>
          </Box>
        )}
      </Box>

      {/* zoom overlay */}
      {zoomUrl && <ZoomModal src={zoomUrl} onClose={()=>setZoomUrl(null)} />}
    </VisibilityTracker>
  );
};

export default LooksPostPage;